import { GemSpec, SubgraphConnectionMap, CommonGraph } from '@common/types';
import { NodeData, StrippedNodeData } from '@prophecy/ui/Graph/types';
import { useSelector } from 'react-redux';
import { Node } from 'reactflow';

import {
  getGraphProcess,
  getGraphProperty,
  getParentGraphPath,
  getParentPathOfProcess,
  isMetaInputGemType,
  isMetaOutputGemType,
  isSubGraphOrMetaGem
} from '../../../Parser/bindings';
import { ReusableSubgraphProperties, WorkflowGraph, WorkflowState } from '../../../redux/types';
import { SUB_GRAPH_NODE_TYPES } from '../../constants';

type isSubgraphFunctionType = (process: CommonGraph) => boolean;
export const isNotSubGraphNode = (node: Node) =>
  !(node.type === SUB_GRAPH_NODE_TYPES.subGraphOutNode || node.type === SUB_GRAPH_NODE_TYPES.subGraphInNode);

export function checkAndFilterMetaGems<N extends NodeData | StrippedNodeData>(_nodes: Node<N>[]) {
  const nodes: Node<N>[] = [];
  let hasMetaInputGem = false;
  let hasMetaOutputGem = false;
  _nodes.forEach((node) => {
    if (isMetaInputGemType(node.data.gemType)) {
      hasMetaInputGem = true;
    } else if (isMetaOutputGemType(node.data.gemType)) {
      hasMetaOutputGem = true;
    } else {
      nodes.push(node);
    }
  });
  return { hasMetaInputGem, hasMetaOutputGem, nodes };
}
export function getSubgraphOutgoingConnectionMap<P extends CommonGraph>(
  processes: P[],
  isSubgraph: isSubgraphFunctionType
) {
  const subgraphOutgoingConnectionMap: { [key: string]: SubgraphConnectionMap } = {};
  for (let index = 0; index < processes.length; index++) {
    const process = processes[index];
    if (isSubgraph(process as CommonGraph)) {
      subgraphOutgoingConnectionMap[process.id] = findSubgraphOutgoingConnections(process, isSubgraph);
    }
  }

  return subgraphOutgoingConnectionMap;
}
export function findSubgraphIncomingConnections<G extends CommonGraph>(
  isSubgraph: isSubgraphFunctionType,
  subgraphId: string,
  graphPath: string,
  rootGraph: G,
  rootPath: string
) {
  const parentPath = getParentPathOfProcess(graphPath, subgraphId);
  const parentGraph: G = getGraphProperty(rootGraph, parentPath, rootPath);
  const connections = parentGraph.connections;
  const map: SubgraphConnectionMap = {};
  for (let index = 0; index < connections.length; index++) {
    const connection = connections[index];
    if (connection.target === subgraphId) {
      const currentProcess = getGraphProcess(
        rootGraph,
        graphPath.replace(`.processes.${subgraphId}`, ''),
        connection.source,
        rootPath
      );

      //Need to check if subgraph is connected to subgraph then check incoming connections of subgraph's last child node
      const isProcessSubgraph = currentProcess && isSubgraph(currentProcess as CommonGraph);
      if (connection.source === parentGraph.id || isProcessSubgraph) {
        // subgraph connected to outer subgraph
        const connectionMap = isProcessSubgraph
          ? findSubgraphOutgoingConnections(currentProcess as unknown as G, isSubgraph)
          : findSubgraphIncomingConnections(isSubgraph, parentGraph.id, parentPath, rootGraph, rootPath);
        const _connection = connectionMap[connection.sourcePort];
        if (_connection) {
          map[connection.targetPort] = {
            source: _connection.source,
            sourcePort: _connection.sourcePort,
            targetPort: _connection.targetPort
          };
        }
      } else {
        map[connection.targetPort] = {
          source: currentProcess,
          sourcePort: connection.sourcePort,
          targetPort: connection.targetPort
        };
      }
    }
  }
  return map;
}
export function findSubgraphOutgoingConnections<G extends CommonGraph>(
  subGraph: G,
  isSubgraph: isSubgraphFunctionType
) {
  const map: SubgraphConnectionMap = {};
  const { connections, id, processes } = subGraph as CommonGraph;
  for (let index = 0; index < connections.length; index++) {
    const connection = connections[index];
    // looking only outgoing connections with in subgraph
    if (connection.target === id) {
      const { source, sourcePort, targetPort } = connection;
      const process = processes[source];
      if (isSubgraph(process as CommonGraph)) {
        // subgraph connected to outer subgraph
        const connectionMap = findSubgraphOutgoingConnections(process as G, isSubgraph);
        const _connection = connectionMap[connection.sourcePort];
        if (_connection) {
          map[connection.targetPort] = {
            source: _connection.source,
            sourcePort: _connection.sourcePort,
            targetPort: _connection.targetPort
          };
        }
      } else {
        map[connection.targetPort] = {
          source: process,
          sourcePort: sourcePort,
          targetPort
        };
      }
    }
  }
  return map;
}
function isGraphReadOnly(
  specs: GemSpec[],
  graph: CommonGraph,
  graphPath: string,
  rootPath: string,
  subgraphs: WorkflowState['subgraphs']
): boolean {
  const currentGraph = getGraphProperty(graph, graphPath, rootPath);
  const isReadOnly =
    isSubGraphOrMetaGem(currentGraph, specs) && isSharedSubgraph(specs, currentGraph, graphPath, rootPath, subgraphs);
  if (isReadOnly) return true;
  // check if parent graph is read only
  const parentPath = getParentGraphPath(graphPath);
  if (parentPath) {
    const parentGraph = getGraphProperty(graph, parentPath, rootPath) as WorkflowGraph;
    if (isSubGraphOrMetaGem(parentGraph, specs)) {
      return isGraphReadOnly(specs, graph, parentPath, rootPath, subgraphs);
    }
  }
  return false;
}
export function useReadOnlyForReusableGraph(specs: GemSpec[], graph: CommonGraph, graphPath: string) {
  const rootPath = useSelector<WorkflowState, string>((state) => state.root);
  const subgraphs = useSelector<WorkflowState, WorkflowState['subgraphs']>((state) => state.subgraphs);
  return isGraphReadOnly(specs, graph, graphPath, rootPath, subgraphs);
}
const isSharedSubgraph = (
  specs: GemSpec[],
  graph: CommonGraph,
  path: string,
  rootPath: string,
  subgraphs: WorkflowState['subgraphs']
) => {
  let isReadOnly = false;
  if (isSubGraphOrMetaGem(graph, specs) && rootPath !== path) {
    const { externalId } = graph.properties as ReusableSubgraphProperties;
    isReadOnly = externalId ? subgraphs[externalId]?.isReadOnly : false;
  }
  return isReadOnly;
};
